// M16 KPI — formül dili: AST + lexer + recursive-descent parser (kpi-formula.ts'ten taşındı, 600
// satır sınırı). Dil tanımı ve kurallar için kpi-formula.ts başlığına bakın; barrel kpi-formula.ts
// üzerinden dışa açar.
import {
  KPI_UNIT_CONVERSIONS,
  isCurrencyUnit,
  normalizeUnitKey,
  unitConversionFactor,
} from "./kpi-units.js";

// ---- AST ---------------------------------------------------------------------
type BinOp = "+" | "-" | "*" | "/";
type Node =
  | { kind: "num"; value: number }
  | { kind: "ref"; code: string }
  | { kind: "neg"; arg: Node }
  | { kind: "bin"; op: BinOp; left: Node; right: Node }
  | { kind: "sum" | "avg" | "prev" | "pctChange"; code: string }
  | { kind: "wavg"; code: string; weightCode: string }
  | { kind: "convert"; code: string; from?: string; to: string }
  // Kırılım (spec §4): bağlamdaki göstergenin hesap grubu üyeleri; `group` boyut slug'ı (D2/D7)
  | { kind: "sumSelf" | "avgSelf"; group?: string };

// Dışarıya opak: tüketici parseFormula'dan alır, kendisi kurmaz.
export type FormulaAst = Node & { readonly __brand?: "FormulaAst" };
// Yalnız kpi-formula-excel.ts için (yazıcı ağacı gezer); tüketici kodu bunu KULLANMAZ.
export type FormulaNode = Node;

export interface FormulaParseOk {
  ok: true;
  ast: FormulaAst;
  refs: string[];
}
export interface FormulaParseErr {
  ok: false;
  error: string;
  position: number;
}

// ---- Lexer (tembel: simgeler istendikçe üretilir → hata konumu her zaman en soldaki suçlu) ----
type OpChar = "+" | "-" | "*" | "/" | "(" | ")" | ";";
type Tok =
  | { t: "num"; v: number; pos: number }
  | { t: "ref"; v: string; pos: number }
  | { t: "fn"; v: string; pos: number }
  | { t: "str"; v: string; pos: number }
  | { t: "op"; v: OpChar; pos: number }
  | { t: "end"; pos: number };

class ParseError extends Error {
  constructor(
    message: string,
    readonly position: number,
  ) {
    super(message);
  }
}

const WORD_RE = /[A-Za-z_\u00C0-\u024F][A-Za-z0-9_\u00C0-\u024F]*/y;
const NUM_RE = /\d+(?:\.\d+)?/y;
const REF_RE = /^[a-z][a-z0-9_]*$/;
/** Grup argümanı = boyut slug'ı (`KpiBreakdownSchema.dimension` ≤40). */
const DIMENSION_RE = /^[a-z][a-z0-9_]{0,39}$/;
const FN_RE = /^[A-Z][A-Z0-9_]*$/;

function foldFunctionName(word: string): string {
  return word
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .toUpperCase();
}

class Lexer {
  private i = 0;
  private buffered: Tok | null = null;
  constructor(private readonly src: string) {}

  peek(): Tok {
    this.buffered ??= this.scan();
    return this.buffered;
  }
  next(): Tok {
    const t = this.peek();
    this.buffered = null;
    return t;
  }

  private scan(): Tok {
    const s = this.src;
    while (this.i < s.length && /\s/.test(s.charAt(this.i))) this.i++;
    const pos = this.i;
    if (pos >= s.length) return { t: "end", pos };
    const ch = s.charAt(pos);
    if ("+-*/();".includes(ch)) {
      this.i++;
      return { t: "op", v: ch as OpChar, pos };
    }
    if (ch === '"') {
      const close = s.indexOf('"', pos + 1);
      if (close < 0) throw new ParseError("Kapanmamış dize (çift tırnak eksik)", pos);
      this.i = close + 1;
      return { t: "str", v: s.slice(pos + 1, close), pos };
    }
    NUM_RE.lastIndex = pos;
    const num = NUM_RE.exec(s);
    if (num) {
      this.i = pos + num[0].length;
      return { t: "num", v: Number(num[0]), pos };
    }
    WORD_RE.lastIndex = pos;
    const word = WORD_RE.exec(s);
    if (word) {
      const w = word[0];
      this.i = pos + w.length;
      if (REF_RE.test(w)) return { t: "ref", v: w, pos };
      const folded = foldFunctionName(w);
      if (FN_RE.test(folded) && w === w.toLocaleUpperCase("tr")) return { t: "fn", v: folded, pos };
      throw new ParseError(
        `Geçersiz tanımlayıcı: ${w} (gösterge kodları küçük harf, fonksiyonlar BÜYÜK harf)`,
        pos,
      );
    }
    throw new ParseError(`Beklenmeyen karakter '${ch}'`, pos);
  }
}

// ---- Parser ------------------------------------------------------------------
const KNOWN_UNITS: ReadonlySet<string> = new Set(
  KPI_UNIT_CONVERSIONS.flatMap((c) => [normalizeUnitKey(c.from), normalizeUnitKey(c.to)]),
);

function describeTok(t: Tok): string {
  switch (t.t) {
    case "end":
      return "Beklenmeyen ifade sonu";
    case "op":
      return `Beklenmeyen simge '${t.v}'`;
    case "str":
      return `Beklenmeyen dize "${t.v}"`;
    case "num":
      return `Beklenmeyen sayı ${t.v.toString()}`;
    case "ref":
      return `Beklenmeyen gösterge kodu ${t.v}`;
    case "fn":
      return `Beklenmeyen fonksiyon ${t.v}`;
  }
}

class Parser {
  private readonly lex: Lexer;
  readonly refs: string[] = [];
  constructor(src: string) {
    this.lex = new Lexer(src);
  }

  parse(): Node {
    const node = this.expr();
    const t = this.lex.peek();
    if (t.t !== "end") throw new ParseError(describeTok(t), t.pos);
    return node;
  }

  private expectOp(v: OpChar, what: string): void {
    const t = this.lex.peek();
    if (t.t === "op" && t.v === v) {
      this.lex.next();
      return;
    }
    throw new ParseError(`${describeTok(t)} — burada '${what}' bekleniyordu`, t.pos);
  }
  private expectRef(): string {
    const t = this.lex.peek();
    if (t.t !== "ref") {
      throw new ParseError(`${describeTok(t)} — burada gösterge kodu bekleniyordu`, t.pos);
    }
    this.lex.next();
    this.addRef(t.v);
    return t.v;
  }
  private expectUnit(): string {
    const t = this.lex.peek();
    if (t.t !== "str") {
      throw new ParseError(`${describeTok(t)} — burada çift tırnaklı birim bekleniyordu`, t.pos);
    }
    this.lex.next();
    if (isCurrencyUnit(t.v))
      throw new ParseError(
        `${t.v}: para birimi çevrilemez (K25 — kur sabit çarpan değildir)`,
        t.pos,
      );
    if (!KNOWN_UNITS.has(normalizeUnitKey(t.v))) {
      throw new ParseError(`Bilinmeyen birim: ${t.v}`, t.pos);
    }
    return t.v;
  }
  private addRef(code: string): void {
    if (!this.refs.includes(code)) this.refs.push(code);
  }

  private expr(): Node {
    let left = this.term();
    for (;;) {
      const t = this.lex.peek();
      if (t.t !== "op" || (t.v !== "+" && t.v !== "-")) return left;
      this.lex.next();
      left = { kind: "bin", op: t.v, left, right: this.term() };
    }
  }
  private term(): Node {
    let left = this.unary();
    for (;;) {
      const t = this.lex.peek();
      if (t.t !== "op" || (t.v !== "*" && t.v !== "/")) return left;
      this.lex.next();
      left = { kind: "bin", op: t.v, left, right: this.unary() };
    }
  }
  /** iç içe derinlik sayacı (güvenlik taraması 2026-10-08: "((((…" ya da "----a" RangeError → 500) */
  private depth = 0;
  private enter(pos: number): void {
    this.depth += 1;
    if (this.depth > MAX_FORMULA_DEPTH) {
      throw new ParseError(`İfade çok iç içe (en fazla ${MAX_FORMULA_DEPTH} seviye)`, pos);
    }
  }
  private leave(): void {
    this.depth -= 1;
  }
  private unary(): Node {
    const t = this.lex.peek();
    if (t.t === "op" && t.v === "-") {
      this.lex.next();
      this.enter(t.pos);
      const arg = this.unary();
      this.leave();
      return { kind: "neg", arg };
    }
    return this.primary();
  }
  private primary(): Node {
    const t = this.lex.peek();
    switch (t.t) {
      case "num":
        this.lex.next();
        return { kind: "num", value: t.v };
      case "ref":
        this.lex.next();
        this.addRef(t.v);
        return { kind: "ref", code: t.v };
      case "fn":
        return this.func();
      case "op":
        if (t.v === "(") {
          this.lex.next();
          this.enter(t.pos);
          const inner = this.expr();
          this.leave();
          this.expectOp(")", ")");
          return inner;
        }
        break;
      default:
        break;
    }
    throw new ParseError(describeTok(t), t.pos);
  }
  private func(): Node {
    const t = this.lex.next();
    if (t.t !== "fn") throw new ParseError(describeTok(t), t.pos);
    const name = t.v;
    if (name === "TOPLA_ALT" || name === "ORT_ALT") return this.selfAggregate(name);
    const simple: Record<string, "sum" | "avg" | "prev" | "pctChange"> = {
      TOPLA: "sum",
      ORT: "avg",
      ONCEKI_YIL: "prev",
      DEGISIM_YUZDE: "pctChange",
    };
    if (name !== "AGIRLIKLI_ORT" && name !== "CEVIR" && !(name in simple)) {
      throw new ParseError(
        `Bilinmeyen fonksiyon: ${name} (geçerli: TOPLA, ORT, TOPLA_ALT, ORT_ALT, AGIRLIKLI_ORT, ONCEKI_YIL, DEGISIM_YUZDE, CEVIR)`,
        t.pos,
      );
    }
    this.expectOp("(", "(");
    const code = this.expectRef();
    if (name === "AGIRLIKLI_ORT") {
      this.expectOp(";", ";");
      const weightCode = this.expectRef();
      this.expectOp(")", ")");
      return { kind: "wavg", code, weightCode };
    }
    if (name === "CEVIR") {
      this.expectOp(";", ";");
      const firstTok = this.lex.peek();
      const first = this.expectUnit();
      const sep = this.lex.peek();
      if (sep.t === "op" && sep.v === ";") {
        this.lex.next();
        const to = this.expectUnit();
        this.expectOp(")", ")");
        if (unitConversionFactor(first, to) === null) {
          throw new ParseError(`Birimler çevrilemez: ${first} → ${to}`, firstTok.pos);
        }
        return { kind: "convert", code, from: first, to };
      }
      this.expectOp(")", ")");
      return { kind: "convert", code, to: first };
    }
    const after = this.lex.peek();
    if (after.t === "op" && after.v === ";") {
      // TOPLA(a; b) — sık hata: TOPLA kategori toplamıdır, kalemleri toplamak '+' ile yapılır
      throw new ParseError(
        `${name} tek argüman alır (kategori ya da gösterge kodu) — kalemleri toplamak için 'a + b' yazın`,
        after.pos,
      );
    }
    this.expectOp(")", ")");
    return { kind: simple[name] ?? "sum", code };
  }
  /**
   * `TOPLA_ALT()` / `TOPLA_ALT("boyut")` / `ORT_ALT(…)`: argüman yok ya da tek çift tırnaklı boyut
   * slug'ı. Gösterge kodu almaz → `refs`'e hiçbir şey eklemez (kendi koduna öz-döngü yok).
   */
  private selfAggregate(name: "TOPLA_ALT" | "ORT_ALT"): Node {
    const kind = name === "TOPLA_ALT" ? "sumSelf" : "avgSelf";
    this.expectOp("(", "(");
    const t = this.lex.peek();
    if (t.t === "op" && t.v === ")") {
      this.lex.next();
      return { kind };
    }
    if (t.t !== "str") {
      throw new ParseError(
        `${describeTok(t)} — ${name} argüman almaz ya da çift tırnaklı boyut alır (ör. ${name}("cinsiyet"))`,
        t.pos,
      );
    }
    this.lex.next();
    if (!DIMENSION_RE.test(t.v)) {
      throw new ParseError(
        `Geçersiz boyut: "${t.v}" (küçük harf, rakam ve alt çizgi — ör. "cinsiyet")`,
        t.pos,
      );
    }
    const after = this.lex.peek();
    if (after.t === "op" && after.v === ";") {
      throw new ParseError(`${name} en fazla bir boyut argümanı alır`, after.pos);
    }
    this.expectOp(")", ")");
    return { kind, group: t.v };
  }
}

/** İfade uzunluğu tavanı — Zod şeması (`KpiFormulaSchema.expression`) ile aynı sayı. */
export const MAX_FORMULA_LENGTH = 2000;
export const MAX_FORMULA_DEPTH = 64;

/**
 * İfadedeki `TOPLA_ALT`/`ORT_ALT` çağrıları: `hasSelf` en az biri var mı; `groups` açık boyut
 * argümanları (ilk görülme sırası, tekil). Kayıt kapısı (D7) argümanı hesap grubu alanına yazar.
 */
export function selfBreakdownCalls(ast: FormulaAst): { hasSelf: boolean; groups: string[] } {
  const out = { hasSelf: false, groups: [] as string[] };
  const visit = (n: Node): void => {
    switch (n.kind) {
      case "sumSelf":
      case "avgSelf":
        out.hasSelf = true;
        if (n.group !== undefined && !out.groups.includes(n.group)) out.groups.push(n.group);
        return;
      case "bin":
        visit(n.left);
        visit(n.right);
        return;
      case "neg":
        visit(n.arg);
        return;
      default:
        return;
    }
  };
  visit(ast);
  return out;
}

export function parseFormula(expression: string): FormulaParseOk | FormulaParseErr {
  if (expression.length > MAX_FORMULA_LENGTH) {
    return {
      ok: false,
      error: `İfade çok uzun (en fazla ${MAX_FORMULA_LENGTH} karakter)`,
      position: MAX_FORMULA_LENGTH,
    };
  }
  const p = new Parser(expression);
  try {
    const ast = p.parse();
    return { ok: true, ast, refs: p.refs };
  } catch (e) {
    if (e instanceof ParseError) return { ok: false, error: e.message, position: e.position };
    if (e instanceof RangeError) return { ok: false, error: "İfade çok karmaşık", position: 0 };
    throw e;
  }
}
